// One frame of the game canvas, back to front: board, square marks, resting
// pieces, pieces in flight, overlay, banner, gear. Pure drawing from a model;
// the scene decides what goes in it.
import { drawPauseGear, PAUSE_GEAR_MARGIN, PAUSE_GEAR_SIZE, type GearIconBounds } from '@bn-games/render-helpers';
import { drawMoveListIcon } from '../render/overlay/MoveListIcon.js';
import { drawBoard } from '../render/BoardRenderer.js';
import { drawPiece, drawPieces } from '../render/PieceRenderer.js';
import { squareToCanvasCenter, type ViewerRotation } from '../render/geometry.js';
import { drawSquareMarks, type BoardMarks } from '../render/overlay/squareMarks.js';
import { drawInteractionOverlay, type InteractionSnapshot } from '../render/overlay/InteractionOverlay.js';
import { drawTurnBanner, type TurnBannerView } from '../render/overlay/TurnBanner.js';
import { drawClocks, type ClockView } from '../render/overlay/ClockDisplay.js';
import type { MoveAnimationFrame } from '../render/PieceAnimator.js';
import type { RenderPalette } from '../render/palette.js';
import { CANVAS_SIZE } from '../utils/constants.js';

/** Everything on the canvas except the gear: what a replay draws too. */
export interface BoardFrame {
  readonly board: Int8Array;
  readonly rotation: ViewerRotation;
  readonly marks: BoardMarks;
  readonly interaction: InteractionSnapshot;
  /** Pieces in flight; their destination squares are skipped on `board`. */
  readonly animation: { readonly frame: MoveAnimationFrame; readonly skip: ReadonlySet<number> } | null;
  readonly banner: TurnBannerView;
  /** Null for an untimed game. */
  readonly clocks: ClockView | null;
}

export interface FrameModel extends BoardFrame {
  readonly gearHovered: boolean;
  readonly moveListHovered: boolean;
  /** Whether the move list is showing: the toggle icon dims when it is not. */
  readonly moveListShown: boolean;
}

/** Where the two corner icons were drawn, for hit-testing. */
export interface FrameIcons {
  readonly gear: GearIconBounds;
  readonly moveList: GearIconBounds;
}

export function renderBoardFrame(ctx: CanvasRenderingContext2D, model: BoardFrame, palette: RenderPalette): void {
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  drawBoard(ctx, palette, model.rotation);
  drawSquareMarks(ctx, model.marks, palette, model.rotation);

  const dragFrom = model.interaction.drag?.from ?? null;
  const skip = model.animation?.skip;
  drawPieces(ctx, model.board, palette, model.rotation, (sq) => sq === dragFrom || (skip?.has(sq) ?? false));

  if (model.animation !== null) {
    for (const fade of model.animation.frame.fades) {
      const { x, y } = squareToCanvasCenter(fade.square, model.rotation);
      drawPiece(ctx, x, y, fade.code, palette, { alpha: fade.alpha });
    }
    for (const slide of model.animation.frame.slides) drawPiece(ctx, slide.at.x, slide.at.y, slide.code, palette);
  } else {
    drawInteractionOverlay(ctx, model.interaction, palette, model.rotation);
  }

  drawTurnBanner(ctx, model.banner, palette);
  if (model.clocks !== null) drawClocks(ctx, model.clocks, palette);
}

export function renderFrame(ctx: CanvasRenderingContext2D, model: FrameModel, palette: RenderPalette): FrameIcons {
  renderBoardFrame(ctx, model, palette);
  // The frame-label ink, not the banner's hairline border: that one is ~1.4:1 against the
  // dark surround, and an icon needs 3:1 (WCAG 1.4.11). This is 9:1 or better in every theme.
  const colors = { fill: palette.bannerBg, border: palette.labelColor };
  const gear = drawPauseGear(ctx, CANVAS_SIZE, colors, { hover: model.gearHovered });
  // The move-list toggle is the gear's left-hand neighbour, at the same size and spacing.
  const moveList = drawMoveListIcon(ctx, gear.x - PAUSE_GEAR_SIZE - PAUSE_GEAR_MARGIN, gear.y, PAUSE_GEAR_SIZE, colors, {
    hover: model.moveListHovered, active: model.moveListShown,
  });
  return { gear, moveList };
}
